import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc as firestoreDoc,
  where,
  serverTimestamp,
  getDoc,
  setDoc,
  runTransaction,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions, storage, COLLECTIONS } from "../config/firebase";
import * as Sentry from "@sentry/react-native";
import AuthService from "./authService";
import { fetchDocsByIds } from "./firestoreBatch";
import { futureEventsCutoffISO } from "../utils/eventHelpers";

// The Cloud Functions callables added for club_members hardening authorize
// super-admin actions via the Auth custom claim (unforgeable), while several
// of these functions historically gated their own "should I even attempt
// this" pre-check on the Firestore users/{uid}.role field instead — a
// separate, client-writable signal that can lag the claim (see
// scripts/set-super-admin-claim.js's own documented propagation delay).
// Without this check, a caller whose Firestore role says super_admin but
// whose claim isn't set/fresh would pass the old gate, perform OTHER writes
// (marking a request/application "approved", creating a club), and only
// then have the callable reject — leaving orphaned, half-approved state
// with no repair tool (the old fixApprovedLeaderMembership/
// fixApprovedApplication utilities were removed as part of this same
// hardening pass, precisely because they had no real authorization of their
// own). Forcing a fresh token read (skipping the client SDK's cache) means a
// claim set moments ago is seen correctly, not just eventually.
async function isCallerActuallySuperAdmin() {
  const user = auth.currentUser;
  if (!user) return false;
  try {
    const tokenResult = await user.getIdTokenResult(true);
    return tokenResult.claims?.superAdmin === true;
  } catch (error) {
    // A forced token refresh is a real network round-trip — degrade to "not
    // super admin" on failure (the caller falls through to the ordinary
    // club-admin check) rather than letting a transient network error take
    // down the whole calling flow. This function's contract is "return a
    // boolean," not "throw."
    if (__DEV__) console.error("isCallerActuallySuperAdmin token refresh failed:", error);
    return false;
  }
}

// Broadcasts the Club of the Week announcement push to every user. Kept as
// a callable (not a direct Firestore write) because the caller's club-admin
// status can't be verified in security rules (club_members uses
// auto-generated doc IDs) — the Cloud Function re-verifies admin status,
// featured_club, and featured_broadcast_sent server-side before fanning out.
const broadcastClubOfTheWeekCallable = httpsCallable(
  functions,
  "broadcastClubOfTheWeek"
);

// club_members write-protection hardening — any write to another user's
// membership (an admin/super-admin acting on someone else's record) now
// goes through one of these, since club_members' auto-generated doc IDs
// mean security rules can't verify "is this uid an admin of clubId" the
// way clubs/{clubId} or a deterministic-ID collection can. Self-writes
// (joining as a plain member, becoming admin of a club you just created)
// stay direct Firestore calls below — see firestore.rules' club_members
// block and firebase-functions/index.js's matching comment for the full
// rationale.
const approveClubMembershipApplicationCallable = httpsCallable(
  functions,
  "approveClubMembershipApplication"
);
const approveClubMembershipApplicationsBulkCallable = httpsCallable(
  functions,
  "approveClubMembershipApplicationsBulk"
);
const approveClubCreationRequestCallable = httpsCallable(
  functions,
  "approveClubCreationRequest"
);
const transferClubAdminMembershipCallable = httpsCallable(
  functions,
  "transferClubAdminMembership"
);
const updateClubMemberRoleCallable = httpsCallable(
  functions,
  "updateClubMemberRoleCallable"
);
const removeClubMemberCallable = httpsCallable(
  functions,
  "removeClubMemberCallable"
);

/**
 * Formats a date to the desired format: "January 20, 2026 at 3:21:56 PM UTC+5"
 */
function formatTimestamp(date = new Date()) {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  // Format time
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  // Format minutes and seconds with leading zeros
  const minutesStr = minutes.toString().padStart(2, "0");
  const secondsStr = seconds.toString().padStart(2, "0");

  // Get timezone offset (e.g., UTC+5)
  // Note: getTimezoneOffset() returns offset in minutes, negative means ahead of UTC
  const timezoneOffset = date.getTimezoneOffset();
  const offsetHours = Math.abs(Math.floor(timezoneOffset / 60));
  const offsetSign = timezoneOffset <= 0 ? "+" : "-";
  // Format as UTC+5 (without minutes, matching the example)
  const timezoneStr = `UTC${offsetSign}${offsetHours}`;

  return `${month} ${day}, ${year} at ${hours}:${minutesStr}:${secondsStr} ${period} ${timezoneStr}`;
}

// KAN-65 gallery hardening: firestore.rules' club_applications create rule
// validates only clubData.name — clubData.gallery rides through unchecked,
// so the ONLY guard against a crafted club_applications doc (submitted
// directly via the Firestore SDK, bypassing HomeScreen's picker/upload UI
// entirely) is this client-side check, applied both where a gallery first
// enters a request (createClubRequest below) and again where an approved
// request's gallery is copied onto the live club doc (reviewApplication's
// approval branch) — a caller could otherwise smuggle an oversized array or
// non-Storage URLs (off-host image injection / tracking-pixel exposure,
// content-spoofing) past the old typeof/length-only filter and have a
// super admin unknowingly publish it to every visitor of that club page.
// Mirrors HomeScreen.js's own MAX_GALLERY_IMAGES cap and the EXACT
// download-URL shape uploadService.js's uploadImageViaREST() builds for a
// staged gallery photo: any https://firebasestorage.googleapis.com host was
// too loose (it would accept a URL from ANY Firebase project's bucket, not
// just this app's) — pin it to this app's own bucket
// (storage.app.options.storageBucket) AND the club-gallery-staging/{uid}/...
// path prefix. The REST download-URL path segment is URL-encoded (uploadUrl
// construction encodes the whole filePath with encodeURIComponent), so "/"
// comes through as "%2F" — the prefix check below matches that encoded form.
const MAX_GALLERY_IMAGES = 8;
function sanitizeGalleryUrls(gallery) {
  if (!Array.isArray(gallery)) return [];
  const bucket = storage.app.options.storageBucket;
  const allowedPrefix = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/club-gallery-staging%2F`;
  return gallery
    .filter(
      (uri) =>
        typeof uri === "string" &&
        uri.length > 0 &&
        uri.startsWith(allowedPrefix)
    )
    .slice(0, MAX_GALLERY_IMAGES);
}

/**
 * SECURITY NOTE (L8): Chat message profanity filtering is currently
 * client-side only (ClubChatScreen.js, EventChatScreen.js). A determined
 * user could bypass it by calling the Firestore SDK directly or modifying
 * the client. Moving to server-side (Cloud Function or Firestore rule with
 * content validation) is recommended for production hardening. Tracked as
 * a known limitation — not addressed in this phase per roadmap decision.
 */

// Default query limit to prevent unbounded Firestore reads.
// The app loads all clubs into state and paginates client-side (HomeScreen),
// so this must exceed the total club count. Current DB has ~412 clubs.
const DEFAULT_QUERY_LIMIT = 1000;

export class ClubService {
  // Get admin user IDs for a club (reusable helper for notifications)
  static async getClubAdminIds(clubId) {
    try {
      const adminQuery = query(
        collection(db, COLLECTIONS.CLUB_MEMBERS),
        where("club_id", "==", clubId),
        where("role", "==", "admin")
      );
      const snapshot = await getDocs(adminQuery);
      // Deduplicate: a user may have multiple membership records for the same club
      return [...new Set(snapshot.docs.map((d) => d.data().user_id))];
    } catch (error) {
      if (__DEV__) console.error("Get club admin IDs error:", error);
      return [];
    }
  }

  // Batched id lookups (services/firestoreBatch.js). Users: members,
  // applications, request lists. Clubs: getUserClubs (strict).
  static _fetchUsersByIds(userIds) {
    return fetchDocsByIds(COLLECTIONS.USERS, userIds);
  }

  // Same, for CLUBS docs: Map<clubId, clubData>.
  static _fetchClubsByIds(clubIds, options) {
    return fetchDocsByIds(COLLECTIONS.CLUBS, clubIds, options);
  }

  /**
   * Notify an applicant that their application was approved or rejected.
   * Handles member applications (club_id set) and club-creation requests (no
   * club yet, so no club_id — the club name comes from the request's
   * clubData and the payload carries request_id instead).
   * Approvals are notified by the approval callables (server-written, in the
   * same transaction), so only a rejection is written here. Its id is random:
   * a predictable id could be pre-created by anyone to suppress the push, and
   * a request can only be rejected once (firestore.rules: pending only).
   */
  static async _notifyApplicationReviewed(applicationData, applicationId, normalizedStatus) {
    try {
      if (normalizedStatus !== "rejected") return;
      const isCreation = applicationData.type === "club_creation";
      const clubId = isCreation ? null : applicationData.clubId || applicationData.club_id;
      const applicantId = applicationData.userId || applicationData.user_id;
      if (!applicantId || (!isCreation && !clubId)) return;

      let clubName;
      if (isCreation) {
        clubName = applicationData.clubData?.name || "your club";
      } else {
        const clubDoc = await getDoc(firestoreDoc(db, COLLECTIONS.CLUBS, clubId));
        clubName = clubDoc.exists() ? clubDoc.data().name : "the club";
      }

      const notifType = "application_rejected";
      const title = "Application Update";
      const message = isCreation
        ? `Your request to create ${clubName} was not approved.`
        : `Your application to ${clubName} was not approved.`;

      await addDoc(collection(db, COLLECTIONS.NOTIFICATIONS), {
        user_id: applicantId,
        sender_id: null,
        ...(clubId ? { club_id: clubId } : {}),
        type: notifType,
        title,
        message,
        data: clubId
          ? { club_id: clubId, type: notifType }
          : { request_id: applicationId, type: notifType },
        created_at: serverTimestamp(),
        read: false,
      });
    } catch (notifError) {
      if (__DEV__) console.error("Application-reviewed notification error:", notifError);
    }
  }

  // Get all clubs
  static async getClubs(filters = {}) {
    try {
      // Only surface clubs that are both verified and active. In this app the
      // UI treats an unverified club as "Inactive" (scraped placeholder clubs
      // are imported with is_verified:false / is_active:true), so is_verified is
      // the real active/inactive signal. The default query adds a member_count
      // order (composite index, below); category/university use their indexes.
      let q = query(
        collection(db, COLLECTIONS.CLUBS),
        where("is_verified", "==", true),
        where("is_active", "==", true)
      );

      // Apply filters
      if (filters.category) {
        q = query(q, where("category", "==", filters.category));
      }
      if (filters.university) {
        q = query(q, where("university", "==", filters.university));
      }

      // Biggest clubs first, so page 1 is the top of Home's list and pages
      // append in order (TF232; index clubs(is_verified, is_active,
      // member_count desc)). Cursor pagination via the previous `lastDoc`.
      if (!filters.category && !filters.university) {
        q = query(q, orderBy("member_count", "desc"));
      }
      if (filters.startAfterDoc) {
        q = query(q, startAfter(filters.startAfterDoc));
      }

      // Apply query limit (L4: prevent unbounded reads)
      const queryLimit = filters.limit || DEFAULT_QUERY_LIMIT;
      q = query(q, limit(queryLimit));

      const querySnapshot = await getDocs(q);

      const clubs = [];
      let lastDoc = null;

      // Process clubs without individual member/event count queries
      for (const docSnapshot of querySnapshot.docs) {
        try {
          const clubData = docSnapshot.data();
          clubs.push({
            id: docSnapshot.id,
            ...clubData,
            // Use stored counts or default to 0
            member_count: clubData.member_count || 0,
            event_count: clubData.event_count || 0,
          });
          lastDoc = docSnapshot;
        } catch {
          // Error processing club
        }
      }

      // A full page suggests there may be more; a short page means we hit the end.
      const hasMore = querySnapshot.docs.length === queryLimit;

      // Apply search filter after fetching (Firestore doesn't support full-text search)
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const filteredClubs = clubs.filter(
          (club) =>
            club.name.toLowerCase().includes(searchTerm) ||
            club.description?.toLowerCase().includes(searchTerm)
        );
        return { data: filteredClubs, error: null, lastDoc, hasMore };
      }

      return { data: clubs, error: null, lastDoc, hasMore };
    } catch (error) {
      if (__DEV__) console.error("Get clubs error:", error);
      return { data: null, error, lastDoc: null, hasMore: false };
    }
  }

  // Just the club doc ({ id, ...data }) — for headers that need name / logo /
  // member_count without getClubById's members + events reads.
  static async getClubSummaryById(clubId) {
    try {
      const snap = await getDoc(firestoreDoc(db, COLLECTIONS.CLUBS, clubId));
      if (!snap.exists()) return { data: null, error: "Club not found" };
      return { data: { id: snap.id, ...snap.data() }, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get club summary error:", error);
      return { data: null, error };
    }
  }

  // Get club by ID (club doc + members + events, read in parallel — KAN-63
  // #6a). Callers: ClubDetailsScreen, QRScannerScreen, AdminScreen gallery
  // reload; header-only callers use getClubSummaryById.
  static async getClubById(clubId) {
    try {
      const clubDocPromise = getDoc(firestoreDoc(db, COLLECTIONS.CLUBS, clubId));
      const membersQuery = query(
        collection(db, COLLECTIONS.CLUB_MEMBERS),
        where("club_id", "==", clubId)
      );
      const membersSnapshotPromise = getDocs(membersQuery);
      // Upcoming only, soonest first (TF232; index events(club_id, start_date)).
      const eventsQuery = query(
        collection(db, COLLECTIONS.EVENTS),
        where("club_id", "==", clubId),
        where("start_date", ">=", futureEventsCutoffISO()),
        orderBy("start_date"),
        limit(DEFAULT_QUERY_LIMIT)
      );
      // Events failure: page still loads; `events: null` = Events tab retries.
      const eventsSnapshotPromise = getDocs(eventsQuery).catch((e) => {
        if (__DEV__) console.error("Club events query failed:", e);
        return null;
      });

      const [clubDoc, membersSnapshot, eventsSnapshot] = await Promise.all([
        clubDocPromise,
        membersSnapshotPromise,
        eventsSnapshotPromise,
      ]);

      if (!clubDoc.exists()) {
        throw new Error("Club not found");
      }

      const clubData = clubDoc.data();

      // Collect all unique user IDs
      const userIds = [];
      const memberDataMap = new Map();
      
      for (const memberDoc of membersSnapshot.docs) {
        const memberData = memberDoc.data();
        if (memberData.user_id && !userIds.includes(memberData.user_id)) {
          userIds.push(memberData.user_id);
        }
        memberDataMap.set(memberDoc.id, {
          id: memberDoc.id,
          role: memberData.role,
          joined_at: memberData.joined_at,
          user_id: memberData.user_id,
        });
      }

      // Fetch all user data in parallel
      const usersById = await this._fetchUsersByIds(userIds);

      // Create a map of user_id to userData
      const userDataMap = new Map();
      usersById.forEach((userData, userId) => {
        userDataMap.set(userId, {
          id: userData.id || userId,
          full_name: userData.full_name,
          email: userData.email,
          avatar_url: userData.avatar_url,
        });
      });

      // Build members array with user data
      const members = [];
      memberDataMap.forEach((memberData) => {
        const userData = userDataMap.get(memberData.user_id);
        if (userData) {
          members.push({
            ...memberData,
            user: userData,
          });
        }
      });

      // Already ordered by start_date (query above).
      const events = eventsSnapshot
        ? eventsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        : null;

      return {
        data: {
          id: clubDoc.id,
          ...clubData,
          members,
          events,
        },
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Get club by ID error:", error);
      return { data: null, error };
    }
  }

  // Apply to join club as a member
  static async applyToClub(clubId, userId, message = "") {
    try {
      // Check if user is already a member, AND check for an existing pending
      // application — two independent reads, run in parallel (KAN-63 #5a).
      const existingMembershipQuery = query(
        collection(db, COLLECTIONS.CLUB_MEMBERS),
        where("club_id", "==", clubId),
        where("user_id", "==", userId)
      );
      // Guard against duplicate applications — check for an existing pending one first.
      // The UI pre-check is non-atomic (race condition on rapid taps), so this
      // service-layer check is the authoritative guard.
      const existingApplicationQuery = query(
        collection(db, COLLECTIONS.CLUB_APPLICATIONS),
        where("clubId", "==", clubId),
        where("userId", "==", userId),
        where("type", "==", "member"),
        where("status", "==", "pending")
      );

      const [existingMembershipSnapshot, existingApplicationSnapshot] =
        await Promise.all([
          getDocs(existingMembershipQuery),
          getDocs(existingApplicationQuery),
        ]);

      if (!existingMembershipSnapshot.empty) {
        return {
          data: null,
          error: { message: "You are already a member of this club" },
        };
      }

      if (!existingApplicationSnapshot.empty) {
        return {
          data: null,
          error: { message: "You already have a pending application for this club" },
        };
      }

      const applicationData = {
        clubId: clubId,
        userId: userId,
        type: "member", // Distinguish from ownership claims
        status: "pending",
        createdAt: serverTimestamp(),
        reviewedAt: null,
        reviewedBy: null,
      };

      // Optional applicant note (KAN Phase 5 "club application form"). Kept
      // out of the payload entirely when blank so the document shape is
      // byte-identical to before this field existed — no
      // application_message: "" ever gets written.
      const trimmedMessage =
        typeof message === "string" ? message.trim().slice(0, 300) : "";
      if (trimmedMessage.length > 0) {
        applicationData.application_message = trimmedMessage;
      }

      const docRef = await addDoc(
        collection(db, COLLECTIONS.CLUB_APPLICATIONS),
        applicationData
      );

      // Notify club admins about the new application — fire-and-forget
      // (KAN-63 #5b) so the caller gets its response as soon as the
      // application doc write above lands, instead of blocking on N admin
      // notification writes. Not awaited; errors are swallowed the same way
      // the previous awaited try/catch swallowed them (same
      // fire-and-forget-with-.catch posture as
      // NotificationService.sendClubNotification(...).catch() in
      // ClubDetailsScreen.js's handleApplyToClub). The deterministic
      // notifDocId scheme below (idempotency) is unchanged.
      (async () => {
        const adminIds = await this.getClubAdminIds(clubId);
        // Get club name and applicant name for the notification
        const clubDoc = await getDoc(firestoreDoc(db, COLLECTIONS.CLUBS, clubId));
        const clubName = clubDoc.exists() ? clubDoc.data().name : "your club";
        const userDoc = await getDoc(firestoreDoc(db, COLLECTIONS.USERS, userId));
        const userName = userDoc.exists()
          ? userDoc.data().full_name || userDoc.data().display_name || "Someone"
          : "Someone";

        // Create in-app notification for admins using a deterministic document ID.
        // ID format: club_application_{applicationId}_{adminId}
        // This makes notification creation idempotent — if applyToClub is somehow
        // called a second time for the same application, setDoc overwrites the same
        // document rather than appending a new one. The Cloud Function only fires on
        // onCreate, so the push notification is also deduplicated automatically.
        for (const adminId of adminIds) {
          const notifDocId = `club_application_${docRef.id}_${adminId}`;
          await setDoc(firestoreDoc(db, COLLECTIONS.NOTIFICATIONS, notifDocId), {
            user_id: adminId,
            sender_id: userId,
            club_id: clubId,
            type: "club_application",
            title: `New Application: ${clubName}`,
            message: `${userName} has applied to join your club`,
            // snake_case club_id + type live inside data so they survive the
            // Cloud Function's push-payload allowlist and the tap handler can
            // deep-link the admin to the club's Applications section.
            data: { club_id: clubId, type: "club_application", applicant_id: userId },
            created_at: serverTimestamp(),
            read: false,
          });
        }

        // e-board gets the same heads-up (admins are handled above, so the
        // roles list deliberately excludes "admin" — no double notification).
        // One source for every apply entry point (club page, Home, QR).
        // application_id keeps the dedup id per-applicant:
        // `club_application_<applicationId>_<eboardUid>`.
        const { NotificationService } = await import("./notificationService");
        await NotificationService.sendClubNotification(clubId, {
          title: "New Club Application",
          message: `${userName} applied to join your club.`,
          data: {
            type: "club_application",
            club_id: clubId,
            application_id: docRef.id,
          },
        }, userId, { roles: ["e-board"] });
      })().catch((notifError) => {
        if (__DEV__) console.error("Application notification error:", notifError);
      });

      return {
        data: { id: docRef.id, ...applicationData },
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Apply to club error:", error);
      return { data: null, error };
    }
  }

  // Cancel/withdraw application to join club
  static async cancelApplication(clubId, userId) {
    try {
      // Find the pending application
      const applicationQuery = query(
        collection(db, COLLECTIONS.CLUB_APPLICATIONS),
        where("clubId", "==", clubId),
        where("userId", "==", userId),
        where("type", "==", "member"),
        where("status", "==", "pending")
      );
      const applicationSnapshot = await getDocs(applicationQuery);

      if (applicationSnapshot.empty) {
        return {
          data: null,
          error: { message: "No pending application found" },
        };
      }

      // Delete the application
      const applicationDoc = applicationSnapshot.docs[0];
      await deleteDoc(applicationDoc.ref);

      return {
        data: { success: true },
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Cancel application error:", error);
      return { data: null, error };
    }
  }

  // Get user's applications
  static async getUserApplications(userId) {
    try {
      // Get all applications for this user and filter in memory
      const applicationsQuery = query(
        collection(db, COLLECTIONS.CLUB_APPLICATIONS),
        where("userId", "==", userId)
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);

      const applications = [];
      applicationsSnapshot.forEach((doc) => {
        const data = doc.data();
        // Only include member applications (an untyped legacy doc is one)
        if ((data.type || "member") === "member") {
          applications.push({
            id: doc.id,
            ...data,
          });
        }
      });

      return { data: applications, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get user applications error:", error);
      return { data: null, error };
    }
  }

  // Get club applications (for admin review)
  static async getClubApplications(clubId) {
    try {
      const applicationsQuery = query(
        collection(db, COLLECTIONS.CLUB_APPLICATIONS),
        where("clubId", "==", clubId)
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);

      // Filter to pending member applications first
      const pendingApps = applicationsSnapshot.docs.filter((docSnapshot) => {
        const d = docSnapshot.data();
        return (d.type === "member" || d.type === undefined) && d.status === "pending";
      });

      // Batched via chunked `in` queries instead of one getDoc per
      // applicant — see _fetchUsersByIds's own comment for why.
      const usersById = await this._fetchUsersByIds(
        pendingApps.map((docSnapshot) => docSnapshot.data().userId)
      );

      const applications = pendingApps.map((docSnapshot) => {
        const applicationData = docSnapshot.data();
        const userData = usersById.get(applicationData.userId) || null;

        let formattedDate = "Unknown date";
        if (applicationData.createdAt) {
          try {
            const date = applicationData.createdAt.toDate
              ? applicationData.createdAt.toDate()
              : new Date(applicationData.createdAt);
            formattedDate = date.toLocaleDateString();
          } catch {
            // Date formatting error
          }
        }

        return {
          id: docSnapshot.id,
          ...applicationData,
          created_at_formatted: formattedDate,
          user: userData
            ? {
                id: applicationData.userId,
                full_name: userData.full_name || userData.display_name || "Unknown User",
                email: userData.email || "No email",
                major: userData.major || "No major",
                graduation_year: userData.graduation_year || "No year",
                avatar_url: userData.avatar_url || userData.photoURL || null,
              }
            : {
                id: applicationData.userId,
                full_name: "Unknown User",
                email: "No email",
                major: "No major",
                graduation_year: "No year",
                avatar_url: null,
              },
        };
      });

      return { data: applications, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get club applications error:", error);
      return { data: null, error };
    }
  }

  // Create club creation request
  static async createClubRequest(requestData) {
    try {
      if (!requestData.userId) {
        return {
          data: null,
          error: { message: "User ID is required" },
        };
      }

      // KAN-65: gallery is an array of already-uploaded Storage download
      // URLs (club-gallery-staging/{uid}/... — see HomeScreen.js's
      // handleSubmitClubRequest). sanitizeGalleryUrls (module-scope above)
      // bounds both the URL shape (Firebase Storage host only) and the
      // count (MAX_GALLERY_IMAGES) — firestore.rules' club_applications
      // create rule does not validate this field, so this is the only gate.
      const gallery = sanitizeGalleryUrls(requestData.gallery);

      const requestDoc = {
        type: "club_creation",
        userId: requestData.userId,
        clubData: {
          name: requestData.name,
          description: requestData.description,
          links: requestData.links || [],
          pdfs: requestData.pdfs || [],
          gallery,
          logo_url: requestData.logo_url || null,
          category: requestData.category || null,
          category_id: requestData.category_id || null,
        },
        status: "pending",
        createdAt: serverTimestamp(),
      };

      const docRef = await addDoc(
        collection(db, COLLECTIONS.CLUB_APPLICATIONS),
        requestDoc
      );

      // Notify super_admins about the new club creation request
      try {
        const { NotificationService } = await import("./notificationService");
        await NotificationService.sendSuperAdminNotification(
          "club_creation_requests",
          {
            title: "New Club Creation Request",
            message: `A student has submitted a request to create a new club: ${requestData.name}`,
            data: {
              type: "club_creation_request",
              application_id: docRef.id,
              club_name: requestData.name,
              user_id: requestData.userId,
            },
          }
        );
      } catch (notificationError) {
        // Don't fail the request creation if notification fails
        if (__DEV__) console.error("Error sending super_admin notification:", notificationError);
      }

      return { data: { id: docRef.id, ...requestDoc }, error: null };
    } catch (error) {
      if (__DEV__) console.error("Create club request error:", error);
      return { data: null, error };
    }
  }

  // Get club creation requests (for super admin review)
  static async getClubCreationRequests() {
    try {
      const requestsQuery = query(
        collection(db, COLLECTIONS.CLUB_APPLICATIONS),
        where("type", "==", "club_creation"), // Only club creation requests
        where("status", "==", "pending")
      );
      const requestsSnapshot = await getDocs(requestsQuery);

      const requests = [];

      // Batch-fetch all requesters' user docs once
      const usersById = await this._fetchUsersByIds(
        requestsSnapshot.docs.map((d) => d.data().userId)
      );

      for (const docSnapshot of requestsSnapshot.docs) {
        const requestData = docSnapshot.data();

        try {
          const userData = usersById.get(requestData.userId) || null;

          // Format the createdAt date properly
          let formattedDate = "Unknown date";
          if (requestData.createdAt) {
            try {
              const date = requestData.createdAt.toDate
                ? requestData.createdAt.toDate()
                : new Date(requestData.createdAt);
              formattedDate = date.toLocaleDateString();
            } catch {
              // Date formatting error
            }
          }

          const requestWithData = {
            id: docSnapshot.id,
            ...requestData,
            created_at_formatted: formattedDate,
            user: userData
              ? {
                  id: requestData.userId,
                  full_name:
                    userData.full_name ||
                    userData.display_name ||
                    "Unknown User",
                  email: userData.email || "No email",
                  major: userData.major || "No major",
                  graduation_year: userData.graduation_year || "No year",
                  avatar_url: userData.avatar_url || userData.photoURL || null,
                }
              : {
                  id: requestData.userId,
                  full_name: "Unknown User",
                  email: "No email",
                  major: "No major",
                  graduation_year: "No year",
                  avatar_url: null,
                },
          };

          requests.push(requestWithData);
        } catch (error) {
          if (__DEV__) console.error(
            "Error fetching data for request:",
            requestData.id,
            error
          );
          // Still include the request even if data fails to load
          requests.push({
            id: docSnapshot.id,
            ...requestData,
            user: null,
          });
        }
      }

      return { data: requests, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get club creation requests error:", error);
      return { data: null, error };
    }
  }

  // Review club creation request (approve/reject)
  static async reviewClubCreationRequest(requestId, action, reviewedBy) {
    try {
      // H11: Require super admin authorization
      if (!reviewedBy) {
        return { data: null, error: { message: "reviewedBy is required for authorization" } };
      }

      // Check the actual, unforgeable custom claim FIRST — approval below
      // grants role:"admin" via a callable that requires this same claim, so
      // failing here (before any write) avoids marking the request
      // "approved" and creating a club that then can't get its admin
      // membership. See isCallerActuallySuperAdmin()'s comment for why this
      // supersedes the old users/{uid}.role read.
      if (action !== "approve" && action !== "reject") {
        return { data: null, error: { message: "Unsupported action" } };
      }

      if (!(await isCallerActuallySuperAdmin())) {
        return { data: null, error: { message: "Unauthorized: super admin required" } };
      }

      // The approval callable is the sole CAPR mutation owner.  In
      // particular, never resume the historical client-side create/write
      // sequence after a timeout: the server may already have committed its
      // canonical result and a direct fallback would recreate the race this
      // callable removes.
      if (action === "approve") {
        const startedAt = Date.now();
        try {
          const response = await approveClubCreationRequestCallable({ requestId });
          return {
            data: {
              ...(response.data || {}),
              outcome: "approved",
              durationMs: Date.now() - startedAt,
            },
            error: null,
          };
        } catch (callableError) {
          // The SDK reports "functions/<code>"; compare the bare code.
          const code = (callableError?.code || "unknown").replace(/^functions\//, "");
          const durationMs = Date.now() - startedAt;
          const message = callableError?.message || "Unable to approve this request";
          if (code === "deadline-exceeded" || code === "unknown" || code === "cancelled") {
            return {
              data: { outcome: "ambiguous", durationMs },
              error: { message, outcome: "ambiguous", retryable: true },
            };
          }
          if (code === "unavailable" || code === "unimplemented") {
            return {
              data: { outcome: "unavailable", durationMs },
              error: { message, outcome: "unavailable", retryable: true },
            };
          }
          return {
            data: { outcome: "rejected", durationMs },
            error: { message, outcome: "rejected", retryable: false },
          };
        }
      }

      // Reject: only a still-pending request can be rejected (firestore.rules
      // enforces the same pending -> rejected transition).
      const requestRef = firestoreDoc(
        db,
        COLLECTIONS.CLUB_APPLICATIONS,
        requestId
      );
      const requestDoc = await getDoc(requestRef);

      if (!requestDoc.exists()) {
        return {
          data: null,
          error: { message: "Request not found" },
        };
      }

      const requestData = requestDoc.data();
      if (requestData.type !== "club_creation") {
        return { data: null, error: { message: "Request not found" } };
      }
      if (requestData.status !== "pending") {
        return { data: null, error: { message: "This request was already reviewed." } };
      }

      await updateDoc(requestRef, {
        status: "rejected",
        reviewedAt: serverTimestamp(),
        reviewedBy: reviewedBy,
      });

      // Tell the applicant (best-effort; never fails the reject).
      await this._notifyApplicationReviewed(requestData, requestId, "rejected");

      return { data: { status: "rejected" }, error: null };
    } catch (error) {
      if (__DEV__) console.error("Review club creation request error:", error);
      return { data: null, error };
    }
  }

  // Review application (approve/reject). `precomputedIsSuperAdmin` lets bulk
  // callers (see reviewApplicationsBulk below) skip the forced token refresh
  // below and reuse one answer across a whole batch.
  static async reviewApplication(applicationId, status, reviewedBy, precomputedIsSuperAdmin) {
    try {
      const applicationRef = firestoreDoc(
        db,
        COLLECTIONS.CLUB_APPLICATIONS,
        applicationId
      );
      const applicationDoc = await getDoc(applicationRef);

      if (!applicationDoc.exists()) {
        return {
          data: null,
          error: { message: "Application not found" },
        };
      }

      const applicationData = applicationDoc.data();

      // H10: reviewedBy is REQUIRED -- no silent skip
      if (!reviewedBy) {
        return { data: null, error: { message: "reviewedBy is required for authorization" } };
      }

      // Member applications only (the leader-claim flow was removed). An
      // untyped legacy doc is a member application.
      if ((applicationData.type || "member") !== "member") {
        return { data: null, error: { message: "Unsupported application type" } };
      }

      // Only a pending application can be reviewed — the server callable and
      // firestore.rules enforce the same, this just fails fast with a clear
      // message instead of a vague permission error.
      if (applicationData.status !== "pending") {
        return {
          data: null,
          error: { message: "This application was already reviewed.", alreadyReviewed: true },
        };
      }

      // Authorization check: reviewedBy must be a club admin / e-board
      // member or a super admin. Super-admin path checks the actual custom
      // claim (see isCallerActuallySuperAdmin()'s comment).
      const isSuperAdmin =
        precomputedIsSuperAdmin !== undefined
          ? precomputedIsSuperAdmin
          : await isCallerActuallySuperAdmin();

      if (!isSuperAdmin) {
        const clubId = applicationData.clubId || applicationData.club_id;
        if (clubId) {
          // club-eboard-permission-parity: approving a member application
          // is open to e-board too.
          const adminQuery = query(
            collection(db, COLLECTIONS.CLUB_MEMBERS),
            where("club_id", "==", clubId),
            where("user_id", "==", reviewedBy),
            where("role", "in", ["admin", "e-board"])
          );
          const adminSnapshot = await getDocs(adminQuery);
          if (adminSnapshot.empty) {
            return { data: null, error: { message: "Unauthorized" } };
          }
        } else {
          return { data: null, error: { message: "Unauthorized" } };
        }
      }

      // Normalize status: "approve" -> "approved", "reject" -> "rejected"
      let normalizedStatus = status;
      if (status === "approve") {
        normalizedStatus = "approved";
      } else if (status === "reject") {
        normalizedStatus = "rejected";
      }
      if (normalizedStatus !== "approved" && normalizedStatus !== "rejected") {
        return { data: null, error: { message: "Unsupported action" } };
      }

      if (normalizedStatus === "approved") {
        // A callable, not a direct write — the reviewer is writing about the
        // applicant, not themselves, which club_members' rules can't verify.
        // The callable creates the membership AND writes the application's
        // approved status in one transaction, so a failure here leaves the
        // application pending and retryable (2026-09-03: 12 approvals
        // aborted on Firestore contention, Sentry PYTHON-DJANGO-12/13).
        try {
          await approveClubMembershipApplicationCallable({
            applicationId,
            clubId: applicationData.clubId,
            userId: applicationData.userId,
            role: "member",
          });
        } catch (joinError) {
          if (__DEV__) console.error("Failed to add user to club:", joinError);
          const code = joinError?.code;
          // The server's own messages for these are written to be shown.
          if (
            code === "functions/failed-precondition" ||
            code === "functions/permission-denied" ||
            code === "functions/not-found"
          ) {
            return {
              data: null,
              error: {
                message: joinError.message || "Could not approve this application.",
              },
            };
          }
          if (code === "functions/aborted") {
            return {
              data: null,
              error: { message: "The club was busy — please try approving again." },
            };
          }
          try {
            Sentry.captureException(joinError, {
              tags: { caught_by: "reviewApplication" },
            });
          } catch (reportError) {
            if (__DEV__) console.error("Sentry report failed:", reportError);
          }
          return {
            data: null,
            error: { message: "Could not approve this application. Please try again." },
          };
        }
      } else {
        await updateDoc(applicationRef, {
          status: normalizedStatus,
          reviewedAt: serverTimestamp(),
          reviewedBy: reviewedBy,
        });
      }

      // SP-6: notify the applicant of the outcome. Reached only on a
      // successful review; failure paths return earlier without notifying.
      await this._notifyApplicationReviewed(
        applicationData,
        applicationId,
        normalizedStatus
      );

      return {
        data: { success: true, status },
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Review application error:", error);
      return { data: null, error };
    }
  }

  // Bulk review (Select All → Approve/Reject in AdminScreen). Computes the
  // caller's super-admin status ONCE via a single forced token refresh, then
  // reuses that answer for every application in the batch — each
  // reviewApplication() call doing its own forced refresh in parallel is
  // what made "Select All" fragile in a way single-item approval never was
  // (N simultaneous forced refreshes for the same user at the same instant).
  static async reviewApplicationsBulk(applicationIds, status, reviewedBy) {
    const normalized =
      status === "approve" || status === "approved" ? "approved" : "rejected";

    const isSuperAdmin = await isCallerActuallySuperAdmin();

    if (normalized === "approved") {
      // FAST path: approve all MEMBER applications in ONE server-side call
      // (approveClubMembershipApplicationsBulk — one transaction per club, no
      // cross-transaction contention, ~one round-trip regardless of count).
      let bulk = null;
      try {
        const res = await approveClubMembershipApplicationsBulkCallable({
          applicationIds,
        });
        bulk = (res && res.data && res.data.results) || {};
      } catch (e) {
        if (__DEV__) console.error("Bulk approve error:", e);
        bulk = null;
      }

      // Total failure: the callable threw. Do NOT reprocess through the
      // per-item path here — a thrown error can also mean the transaction
      // committed but its response was lost, and re-running the unguarded
      // reviewApplication would (a) re-approve an application another reviewer
      // just rejected and (b) reintroduce the exact club_members write
      // contention this function exists to remove. Surface a retryable error
      // instead; a retry re-runs the safe bulk path, which re-reads status in
      // its transaction. (Per-application single approval is unaffected.)
      if (bulk === null) {
        return applicationIds.map(() => ({
          data: null,
          error: { message: "Bulk approval failed — please try again." },
        }));
      }

      const out = [];
      for (const id of applicationIds) {
        const r = bulk[id];
        if (r === "approved") {
          out.push({ data: { success: true, status }, error: null });
        } else if (r === "not_pending") {
          // Already reviewed by someone else — not a failure, and the row
          // must stay gone. alreadyReviewed lets the caller skip the
          // "member added" local patch.
          out.push({ data: { success: true, status, alreadyReviewed: true }, error: null });
        } else {
          // A definitive non-approved server outcome (not_found /
          // unauthorized / invalid / changed / error). Do NOT re-approve — a
          // fallback here would overwrite e.g. a concurrent rejection.
          out.push({
            data: null,
            error: { message: `Not approved (${r || "unknown"})` },
          });
        }
      }
      return out;
    }

    // Reject (and anything else): per-item, sequentially. Reject only updates
    // application status docs (a different doc per application), so it never hit
    // the club_members / member_count contention that parallel approve did.
    const out = [];
    for (const id of applicationIds) {
      const result = await this.reviewApplication(id, status, reviewedBy, isSuperAdmin);
      out.push(
        result.error?.alreadyReviewed
          ? { data: { success: true, status, alreadyReviewed: true }, error: null }
          : result
      );
    }
    return out;
  }

  // Update club
  static async updateClub(clubId, updates, userId) {
    try {
      // H2: Require authorization
      if (!userId) {
        return { data: null, error: { message: "userId is required for authorization" } };
      }

      // Check if user is club admin or e-board (club-eboard-permission-parity)
      const adminQuery = query(
        collection(db, COLLECTIONS.CLUB_MEMBERS),
        where("club_id", "==", clubId),
        where("user_id", "==", userId),
        where("role", "in", ["admin", "e-board"])
      );
      const adminSnapshot = await getDocs(adminQuery);
      const isClubAdmin = !adminSnapshot.empty;

      // Check if user is super admin (only if not already a club admin)
      let isSuperAdmin = false;
      if (!isClubAdmin) {
        const userDoc = await getDoc(firestoreDoc(db, COLLECTIONS.USERS, userId));
        if (userDoc.exists() && userDoc.data().role === "super_admin") {
          isSuperAdmin = true;
        }
      }

      if (!isClubAdmin && !isSuperAdmin) {
        return { data: null, error: { message: "Unauthorized" } };
      }

      // Field allowlist for club updates
      const ALLOWED_CLUB_UPDATE_FIELDS = [
        'name',
        'description',
        'links',
        'pdfs',
        'gallery',
        'logo_url',
        'category',
        'category_id',
        'contact_email',
        'website_url',
        'is_active',
      ];

      // Super admins can also update verification status
      if (isSuperAdmin) {
        ALLOWED_CLUB_UPDATE_FIELDS.push('is_verified');
      }

      const sanitized = {};
      for (const key of Object.keys(updates)) {
        if (ALLOWED_CLUB_UPDATE_FIELDS.includes(key)) {
          sanitized[key] = updates[key];
        }
      }

      // Defensive shape check: gallery must be an array of non-empty strings.
      if ("gallery" in sanitized) {
        sanitized.gallery = Array.isArray(sanitized.gallery)
          ? sanitized.gallery.filter(
              (uri) => typeof uri === "string" && uri.length > 0
            )
          : [];
      }

      if (Object.keys(sanitized).length === 0) {
        return { data: null, error: { message: "No valid fields to update" } };
      }

      const clubRef = firestoreDoc(db, COLLECTIONS.CLUBS, clubId);
      await updateDoc(clubRef, {
        ...sanitized,
        updated_at: formatTimestamp(new Date()),
      });

      const updatedDoc = await getDoc(clubRef);
      return { data: { id: updatedDoc.id, ...updatedDoc.data() }, error: null };
    } catch (error) {
      if (__DEV__) console.error("Update club error:", error);
      return { data: null, error };
    }
  }

  // Transfer admin control to another member
  static async transferAdminControl(clubId, currentAdminId, newAdminId) {
    try {
      // Verify the calling user is the currentAdminId
      const { user: callingUser } = await AuthService.getCurrentUser();
      if (!callingUser || callingUser.uid !== currentAdminId) {
        return {
          data: null,
          error: { message: "Unauthorized: caller must be the current admin" },
        };
      }

      // L3: atomically update both roles — a callable, not a direct batch
      // write. This writes the new admin's role for THEM, which
      // club_members' rules can't verify as coming from an authorized
      // party; the callable re-verifies caller==currentAdminId and their
      // admin status server-side, then does both updates in one batch.
      // Item 16 (2026-09-26): the two advisory membership pre-checks that
      // used to live here (current-admin role, new-admin membership) are
      // dropped — the callable's own transaction re-reads both querysets
      // fresh and is the actual authorization/consistency boundary, so
      // these were only ever adding two extra round trips before the call
      // that already re-verifies everything.
      try {
        await transferClubAdminMembershipCallable({
          clubId,
          currentAdminId,
          newAdminId,
        });
      } catch (transferError) {
        if (__DEV__) console.error("Transfer admin control error:", transferError);
        return { data: null, error: transferError };
      }

      // Item 16 (2026-09-26): the "you now have admin access" notification
      // used to be an awaited client write here (this._notifyAdminAccessGranted)
      // after the callable resolved. transferClubAdminMembership now writes
      // it server-side, inside the same call, via writeRoleGrantedNotification
      // — no client round trip needed.

      return {
        data: {
          success: true,
          message: "Admin control transferred successfully",
        },
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Transfer admin control error:", error);
      return { data: null, error };
    }
  }

  // Leave club
  // Handles both "user leaves their own club" (ClubDetailsScreen) and "admin
  // removes a different member" (AdminScreen/ManageUsersScreen) — same
  // signature, both callers always have. Self-removal stays a direct client
  // delete (the new club_members rule permits deleting your own record);
  // removing someone ELSE now goes through a callable, since club_members'
  // rules can't verify "is this caller actually an admin of this club" —
  // and note this callable is also the first real server-side enforcement
  // of that check at all, for this specific action (the direct-delete
  // version below never verified caller identity beyond `isAuth()`).
  static async leaveClub(clubId, userId) {
    try {
      const { user: callingUser } = await AuthService.getCurrentUser();
      const isSelf = callingUser && callingUser.uid === userId;

      if (!isSelf) {
        await removeClubMemberCallable({ clubId, targetUserId: userId });
        return { error: null };
      }

      const membersQuery = query(
        collection(db, COLLECTIONS.CLUB_MEMBERS),
        where("club_id", "==", clubId),
        where("user_id", "==", userId)
      );
      const membersSnapshot = await getDocs(membersQuery);

      if (!membersSnapshot.empty) {
        // Delete every matching doc, not just the first — club_members
        // still lacks deterministic IDs, so a pre-existing duplicate row
        // could otherwise survive a "Leave Club" tap while the member count
        // silently drifts from decrementing by a hardcoded 1.
        await Promise.all(
          membersSnapshot.docs.map((memberDoc) => deleteDoc(memberDoc.ref))
        );

        // Update club member count
        await this.updateClubMemberCount(clubId, -membersSnapshot.size);
      }

      return { error: null };
    } catch (error) {
      if (__DEV__) console.error("Leave club error:", error);
      return { error };
    }
  }

  // Check if user is member of club
  static async isMember(clubId, userId) {
    try {
      const membersQuery = query(
        collection(db, COLLECTIONS.CLUB_MEMBERS),
        where("club_id", "==", clubId),
        where("user_id", "==", userId)
      );
      const membersSnapshot = await getDocs(membersQuery);

      return { isMember: !membersSnapshot.empty, error: null };
    } catch (error) {
      if (__DEV__) console.error("Check membership error:", error);
      return { isMember: false, error };
    }
  }

  // Get user's clubs
  static async getUserClubs(userId) {
    try {
      const membersQuery = query(
        collection(db, COLLECTIONS.CLUB_MEMBERS),
        where("user_id", "==", userId)
      );
      const membersSnapshot = await getDocs(membersQuery);

      // Chunked documentId() "in" lookup instead of one getDoc per membership
      const memberDatas = membersSnapshot.docs.map((d) => d.data());
      // strict: a failed chunk is an error (callers keep their last good
      // list) rather than a silently shorter membership list.
      const clubsById = await this._fetchClubsByIds(memberDatas.map((m) => m.club_id), { strict: true });
      const clubs = [];
      for (const memberData of memberDatas) {
        const clubData = clubsById.get(memberData.club_id);
        if (clubData) {
          clubs.push({ id: memberData.club_id, ...clubData, member_role: memberData.role });
        }
      }

      return { data: clubs, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get user clubs error:", error);
      return { data: null, error };
    }
  }

  // Update club member count (atomic transaction — prevents race conditions and negative counts)
  static async updateClubMemberCount(clubId, incrementValue) {
    try {
      const clubRef = firestoreDoc(db, COLLECTIONS.CLUBS, clubId);
      await runTransaction(db, async (transaction) => {
        const clubDoc = await transaction.get(clubRef);
        if (!clubDoc.exists()) return;
        const currentCount = clubDoc.data().member_count || 0;
        const newCount = Math.max(0, currentCount + incrementValue);
        transaction.update(clubRef, { member_count: newCount });
      });
      return { success: true, error: null };
    } catch (error) {
      if (__DEV__) console.error("Update club member count error:", error);
      return { success: false, error };
    }
  }

  // Sync member count with actual club_members documents
  static async syncMemberCount(clubId) {
    try {
      const membersQuery = query(
        collection(db, COLLECTIONS.CLUB_MEMBERS),
        where("club_id", "==", clubId)
      );
      const membersSnapshot = await getDocs(membersQuery);
      const actualCount = membersSnapshot.size;

      const clubRef = firestoreDoc(db, COLLECTIONS.CLUBS, clubId);
      const clubDoc = await getDoc(clubRef);
      if (clubDoc.exists() && clubDoc.data().member_count !== actualCount) {
        await updateDoc(clubRef, { member_count: actualCount });
      }
      return { data: actualCount, error: null };
    } catch (error) {
      if (__DEV__) console.error("Sync member count error:", error);
      return { data: null, error };
    }
  }

  // Update club event count (transactional — prevents race and negative counts)
  static async updateClubEventCount(clubId, incrementValue) {
    try {
      const clubRef = firestoreDoc(db, COLLECTIONS.CLUBS, clubId);

      // M9: Read and write atomically inside a transaction to eliminate the
      // read-then-write race and the post-hoc negative guard
      await runTransaction(db, async (transaction) => {
        const clubDoc = await transaction.get(clubRef);
        if (!clubDoc.exists()) return;

        const currentCount = clubDoc.data().event_count || 0;
        const newCount = Math.max(currentCount + incrementValue, 0);
        transaction.update(clubRef, { event_count: newCount });
      });
    } catch (error) {
      if (__DEV__) console.error("Update club event count error:", error);
    }
  }

  // Get club members
  static async getClubMembers(clubId) {
    try {
      const membersQuery = query(
        collection(db, COLLECTIONS.CLUB_MEMBERS),
        where("club_id", "==", clubId)
      );
      const membersSnapshot = await getDocs(membersQuery);

      const memberDocs = membersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // Batched via chunked `in` queries instead of one getDoc per member —
      // see _fetchUsersByIds's own comment for why.
      const usersById = await this._fetchUsersByIds(
        memberDocs.map((m) => m.user_id)
      );

      const members = [];
      memberDocs.forEach((memberData) => {
        const userData = usersById.get(memberData.user_id) || null;

        if (userData) {
          members.push({
            id: memberData.id,
            user_id: memberData.user_id,
            role: memberData.role,
            joined_at: memberData.joined_at,
            user: {
              id: userData.id || memberData.user_id,
              full_name: userData.full_name,
              email: userData.email,
              major: userData.major,
              graduation_year: userData.graduation_year,
              avatar_url: userData.avatar_url || userData.photoURL,
            },
          });
        }
      });

      return { data: members, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get club members error:", error);
      return { data: null, error };
    }
  }

  // Get user membership for a specific club
  static async getUserMembership(clubId, userId) {
    try {
      const membershipQuery = query(
        collection(db, COLLECTIONS.CLUB_MEMBERS),
        where("club_id", "==", clubId),
        where("user_id", "==", userId)
      );
      const membershipSnapshot = await getDocs(membershipQuery);

      const memberships = membershipSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return { data: memberships, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get user membership error:", error);
      return { data: null, error };
    }
  }

  // Get club chat
  static async getClubChat(clubId) {
    try {
      const chatQuery = query(
        collection(db, COLLECTIONS.CLUB_CHATS),
        where("club_id", "==", clubId)
      );

      const chatSnapshot = await getDocs(chatQuery);

      if (chatSnapshot.empty) {
        return { data: null, error: "Club chat not found" };
      }

      const chatDoc = chatSnapshot.docs[0];
      const chatData = chatDoc.data();

      return {
        data: {
          id: chatDoc.id,
          ...chatData,
        },
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Error getting club chat:", error);
      return { data: null, error };
    }
  }

  // Update a member's role in a club (caller must be admin)
  static async updateMemberRole(clubId, userId, newRole) {
    try {
      // Validate role against allowed values
      const ALLOWED_ROLES = ["member", "e-board", "admin"];
      if (!ALLOWED_ROLES.includes(newRole)) {
        return {
          data: null,
          error: { message: `Invalid role: "${newRole}". Allowed: ${ALLOWED_ROLES.join(", ")}` },
        };
      }

      // Verify caller is an admin of this club
      const { user: currentUser } = await AuthService.getCurrentUser();
      if (!currentUser) {
        return {
          data: null,
          error: { message: "User not authenticated" },
        };
      }

      // Check if caller is club admin OR super_admin
      const callerMemberQuery = query(
        collection(db, COLLECTIONS.CLUB_MEMBERS),
        where("club_id", "==", clubId),
        where("user_id", "==", currentUser.uid)
      );
      const callerSnapshot = await getDocs(callerMemberQuery);

      const isClubAdmin = !callerSnapshot.empty && callerSnapshot.docs[0].data().role === "admin";

      // Also check if caller is super_admin — the actual custom claim (see
      // isCallerActuallySuperAdmin()'s comment), not the Firestore role
      // field: updateClubMemberRoleCallable below requires this same claim,
      // so checking the weaker field here would let this pass locally and
      // then fail confusingly at the callable for a caller who genuinely
      // isn't authorized by the signal that actually matters.
      let isSuperAdmin = false;
      if (!isClubAdmin) {
        isSuperAdmin = await isCallerActuallySuperAdmin();
      }

      if (!isClubAdmin && !isSuperAdmin) {
        return {
          data: null,
          error: { message: "Only club admins or super admins can update member roles" },
        };
      }

      // Find the target member
      const targetMemberQuery = query(
        collection(db, COLLECTIONS.CLUB_MEMBERS),
        where("club_id", "==", clubId),
        where("user_id", "==", userId)
      );
      const targetSnapshot = await getDocs(targetMemberQuery);

      if (targetSnapshot.empty) {
        return {
          data: null,
          error: { message: "User is not a member of this club" },
        };
      }

      // A callable, not a direct write — the caller is changing SOMEONE
      // ELSE's role, which club_members' rules can't verify.
      try {
        await updateClubMemberRoleCallable({
          clubId,
          targetUserId: userId,
          newRole,
        });
      } catch (updateError) {
        if (__DEV__) console.error("Update member role error:", updateError);
        return { data: null, error: updateError };
      }

      // Item 16 (2026-09-26): the "promoted to club admin" notification used
      // to be an awaited client write here (this._notifyAdminAccessGranted)
      // for newRole==="admin". That role change now only ever happens
      // through transferAdminControl (updateClubMemberRoleCallable rejects
      // a direct newRole:"admin" call — "Use the admin-transfer flow"), and
      // transferClubAdminMembership writes this notification server-side.

      return {
        data: { success: true, userId, newRole },
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Update member role error:", error);
      return { data: null, error };
    }
  }

  // Add a bio item to a club's bio_items subcollection
  static async addBioItem(clubId, itemData) {
    try {
      const bioItem = {
        title: itemData.title,
        url: itemData.url,
        type: itemData.type,
        is_members_only: itemData.is_members_only || false,
        created_at: serverTimestamp(),
      };

      const bioItemsRef = collection(db, COLLECTIONS.CLUBS, clubId, "bio_items");
      const docRef = await addDoc(bioItemsRef, bioItem);

      return { data: { id: docRef.id, ...bioItem }, error: null };
    } catch (error) {
      if (__DEV__) console.error("Add bio item error:", error);
      return { data: null, error };
    }
  }

  // Get all categories from clubs_categories collection
  static async getCategories() {
    try {
      const categoriesQuery = query(
        collection(db, COLLECTIONS.CLUBS_CATEGORIES),
        orderBy("order", "asc")
      );
      const categoriesSnapshot = await getDocs(categoriesQuery);

      const categories = [];
      for (const docSnapshot of categoriesSnapshot.docs) {
        const categoryData = docSnapshot.data();
        categories.push({
          id: categoryData.id || docSnapshot.id, // Use stored id field if available, fallback to doc id
          name: categoryData.name,
          order: categoryData.order || 0,
          numberOfClubs: categoryData.numberOfClubs || 0,
        });
      }

      return { data: categories, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get categories error:", error);
      return { data: null, error };
    }
  }

  // Club of the Week: admin-triggered "announce to everyone" broadcast for
  // the current winning club. The Cloud Function re-checks everything this
  // call implies (caller is an admin of clubId, clubId is the current
  // featured_club, hasn't already broadcast) — this wrapper is just plumbing.
  static async broadcastClubOfTheWeek(clubId, eventId) {
    try {
      const result = await broadcastClubOfTheWeekCallable({ clubId, eventId });
      if (result.data?.success !== true) {
        throw new Error("Broadcast was not confirmed");
      }
      return { data: result.data, error: null };
    } catch (error) {
      if (__DEV__) console.error("Broadcast Club of the Week error:", error);
      return { data: null, error };
    }
  }
}
